import { usePackageBookingStore } from "@elixir/stores";
import {
  PACKAGE_POPUP_TYPE,
  PACKAGE_PRICING_MODEL,
  PACKAGE_TYPE,
} from "@elixir/types";
import {
  formatSessionsToInclusions,
  formattedPackageDetails,
} from "@elixir/utils";
import { Drawer, notification } from "antd";
import React from "react";
import { ModalFooter, DrawerHeaderUi } from "ui";
import useUpdateAPackage from "../../../../apis/tryst/packages-and-treatments/use-update-a-package";
import { track } from "../../../../utils/tracking";
import PackageBreakdown from "../popup-content/package-breakdown";
import SessionDetails from "../popup-content/session-details";

const EditPackagePopup = () => {
  React.useEffect(() => {
    track("edit_package_popup");
  }, []);

  const packageDetails = usePackageBookingStore(
    (state) => state.packageDetails
  );
  const packagePopupType = usePackageBookingStore(
    (state) => state.packagePopupType
  );
  const clearPackageDetails = usePackageBookingStore(
    (state) => state.clearPackageDetails
  );

  const sessionList = usePackageBookingStore((state) => state.sessionList);
  const setSessionList = usePackageBookingStore(
    (state) => state.setSessionList
  );

  React.useEffect(() => {
    if (packageDetails?.inclusions) {
      setSessionList(formattedPackageDetails(packageDetails?.inclusions));
    } else {
      setSessionList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageDetails?.inclusions]);

  const handleClosePopup = () => {
    clearPackageDetails();
  };

  const isPricingModelNotPresent = !packageDetails?.pricing_model;
  const isSessionListEmpty = sessionList.length === 0;
  const isPricingModelOneTime =
    PACKAGE_PRICING_MODEL.ONE_TIME === packageDetails?.pricing_model;
  const isTotalPriceNotPresent = isPricingModelOneTime
    ? !packageDetails?.total_price
    : false;
  const isPackageNameNotPresent = !packageDetails?.package_name;
  const isPackageSessionNameNotPresent = sessionList.some(
    (session) => !session.name
  );
  const isPackageTypeNotPresent = !packageDetails?.type;
  const isPackageTypeSubscriptionPlan =
    packageDetails?.type === PACKAGE_TYPE.SUBSCRIPTION_PLAN;
  const isPackageExpiryNotPresent =
    isPackageTypeSubscriptionPlan &&
    !packageDetails?.package_expiry_from_start &&
    packageDetails?.package_expiry_from_start !== 0;
  const isPackageFamilyMembersNotPresent =
    isPackageTypeSubscriptionPlan &&
    (packageDetails?.max_members < 1 || !packageDetails?.max_members);

  const isDisabled =
    isSessionListEmpty ||
    isPackageNameNotPresent ||
    isTotalPriceNotPresent ||
    isPricingModelNotPresent ||
    isPackageSessionNameNotPresent ||
    isPackageTypeNotPresent ||
    isPackageExpiryNotPresent ||
    isPackageFamilyMembersNotPresent;

  const {
    mutateAsync: updateAPackageConfig,
    isLoading: updatePackageConfigLoading,
  } = useUpdateAPackage();

  const handleOkClick = async () => {
    if (!packageDetails) return;
    const response = await updateAPackageConfig({
      data: {
        ...packageDetails,
        package_id: packageDetails.package_id as string,
        inclusions: formatSessionsToInclusions(sessionList),
        number_of_inclusions: sessionList?.length,
        total_price:
          packageDetails?.pricing_model === PACKAGE_PRICING_MODEL.ONE_TIME
            ? packageDetails?.total_price
            : undefined,
        max_members:
          packageDetails?.type === PACKAGE_TYPE.SUBSCRIPTION_PLAN
            ? packageDetails?.max_members
            : 1,
        package_expiry_from_start:
          packageDetails?.type === PACKAGE_TYPE.SUBSCRIPTION_PLAN
            ? packageDetails?.package_expiry_from_start
            : undefined,
      },
    });
    if (response?.success) {
      notification.success({
        message: "Package updated successfully",
        placement: "bottomRight",
      });
      handleClosePopup();
    } else {
      notification.error({
        message: "Package update failed",
        placement: "bottomRight",
      });
    }
  };

  return (
    <Drawer
      bodyStyle={{ padding: 0 }}
      closable={false}
      placement={"right"}
      onClose={handleClosePopup}
      open={packagePopupType === PACKAGE_POPUP_TYPE.EDIT_PACKAGE}
      width={"90%"}
      extra={null}
      destroyOnClose={true}
      footer={
        <ModalFooter
          isOkButtonDisabled={isDisabled}
          onCancel={handleClosePopup}
          onOk={handleOkClick}
          isSubmitting={updatePackageConfigLoading}
          okText={
            packageDetails?.type === PACKAGE_TYPE.TREATMENT_PLAN
              ? "Update Treatment Package"
              : "Update Subscription Package"
          }
        />
      }
    >
      <DrawerHeaderUi
        title={
          packageDetails?.type === PACKAGE_TYPE.TREATMENT_PLAN
            ? "Edit Treatment Package"
            : "Edit Subscription Package"
        }
        onClose={handleClosePopup}
      />
      <div className="dr-bg-darwin-neutral-50 dr-h-full dr-p-16 dr-flex dr-flex-col dr-gap-y-24">
        <PackageBreakdown />
        {/* NOTE: It is mandatory to have at least one session associated with a package */}
        <SessionDetails pricingModel={packageDetails?.pricing_model} />
      </div>
    </Drawer>
  );
};

export default EditPackagePopup;
